import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
   CALL_SWITCH_ACCEPT,
   CALL_SWITCH_CANCEL,
   CALL_SWITCH_DECLINE,
   CALL_SWITCH_REQ_WAITING,
   CALL_SWITCH_REQUEST,
   CALL_SWITCH_REQUEST_INIT,
   CALL_TYPE,
   CALLKIT_CALLSTATUS,
} from '../constants';
import { switchVideo, toggleVideo } from '../services/callService';
import { updateCallSwitch, setCallType, useCallkitDataKeys } from '../model';
import { logger, showErrorToast, showInfoToast, showWarningToast } from '@/shared/utils';
import { useLanguage } from '@/app/providers';
import { isOwnUserReconnecting, getLocalUserName } from '../utils/helper';

const EMPTY_OBJ = {};

export const useVideoCallSwitchPopup = () => {
   const dispatch = useDispatch();
   const { t } = useLanguage();
   const { callSwitchState, ongoingCallParticipants } = useCallkitDataKeys([
      'callSwitchState',
      'ongoingCallParticipants',
   ]);
   const { switchAction, fromUser } = callSwitchState;
   const [showPopup, setShowPopup] = useState(false);
   const [callSwitchData, setCallSwitchData] = useState({});
   const [isProcessing, setIsProcessing] = useState(false);
   const [iRequestedSwitch, setIRequestedSwitch] = useState(false);
   const audioRef = useRef(null);

   const localUserId = getLocalUserName();
   const localUserStatus = ongoingCallParticipants?.find(p => p.userId === localUserId)?.callStatus;
   const isReconnecting = localUserStatus === CALLKIT_CALLSTATUS.RECONNECTING;

   useEffect(() => {
      const isIncomingRequest = switchAction === CALL_SWITCH_REQUEST;

      if (isIncomingRequest && !isReconnecting && showPopup) {
         if (!audioRef.current) {
            audioRef.current = new Audio('/sounds/callSwitchRequest.mp3');
            audioRef.current.loop = true;
         }
         audioRef.current.play().catch(err => logger.warn('Switch request audio play failed:', err));
      } else if (audioRef.current) {
         audioRef.current.pause();
         audioRef.current.currentTime = 0;
      }

      return () => {
         if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
         }
      };
   }, [switchAction, isReconnecting, showPopup]);

   const users = useSelector(state => state.userInfoData?.users || EMPTY_OBJ);

   const getNickName = userId => {
      return users[userId]?.nickName || userId;
   };

   useEffect(() => {
      // Sync showPopup state with switchAction
      if (switchAction && !showPopup) {
         setShowPopup(true);
      } else if (!switchAction && showPopup) {
         setShowPopup(false);
      }

      if (switchAction) {
         switch (switchAction) {
            case CALL_SWITCH_REQUEST_INIT:
               setCallSwitchData({
                  content: t('common.VIDEO_SWITCH_CONFIRM'),
                  cancelBtnLabel: t('common.CANCEL'),
                  confirmBtnLabel: t('common.SWITCH'),
                  confirmStatusToUpdate: CALL_SWITCH_REQ_WAITING,
                  cancelStatusToUpdate: CALL_SWITCH_CANCEL,
               });
               break;
            case CALL_SWITCH_REQ_WAITING:
               setCallSwitchData({
                  content: t('common.VIDEO_SWITCH_REQUESTING'),
                  cancelBtnLabel: t('common.CANCEL'),
                  cancelStatusToUpdate: CALL_SWITCH_CANCEL,
               });
               break;
            case CALL_SWITCH_REQUEST:
               setCallSwitchData({
                  content: t('common.VIDEO_SWITCH_REQUEST'),
                  cancelBtnLabel: t('common.DECLINE'),
                  cancelStatusToUpdate: CALL_SWITCH_DECLINE,
                  confirmBtnLabel: t('common.ACCEPT'),
                  confirmStatusToUpdate: CALL_SWITCH_ACCEPT,
               });
               break;
            case CALL_SWITCH_DECLINE:
               if (iRequestedSwitch) {
                  const username = fromUser ? getNickName(fromUser) : '';
                  const msg = fromUser
                     ? t('common.VIDEO_SWITCH_DECLINED_BY', { username })
                     : t('common.VIDEO_SWITCH_DECLINED_BY_USER');
                  showInfoToast(msg);
               }
               setIRequestedSwitch(false);
               handleCloseAndResetPopup();
               break;
            case CALL_SWITCH_CANCEL:
               handleCloseAndResetPopup();
               break;
            default:
               setCallSwitchData({});
         }
      } else {
         setShowPopup(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [switchAction, fromUser, iRequestedSwitch, dispatch, users, showPopup, t]);

   useEffect(() => {
      if (switchAction === CALL_SWITCH_REQ_WAITING) {
         if (!iRequestedSwitch) setIRequestedSwitch(true);
      }
   }, [switchAction, iRequestedSwitch]);

   // Dismiss switch popup if more than 2 participants are in the call
   useEffect(() => {
      const actualParticipantsCount = ongoingCallParticipants?.filter(p => !p.userId?.endsWith('-screen')).length || 0;
      if (actualParticipantsCount > 2 && switchAction) {
         handleCloseAndResetPopup();
      }
   }, [ongoingCallParticipants, switchAction]);

   const handleCloseAndResetPopup = () => {
      setShowPopup(false);
      setCallSwitchData({});
      setIRequestedSwitch(false);

      dispatch(
         updateCallSwitch({
            switchAction: null,
            fromUser: null,
         }),
      );
   };

   const handleSwitchAction = actionType => {
      if (isOwnUserReconnecting()) {
         showWarningToast(t('common.INTERNET_ERROR_MESSAGE'));
         return;
      }
      if (isProcessing) return;
      setIsProcessing(true);

      if (switchAction === CALL_SWITCH_REQUEST_INIT) {
         if (actionType === CALL_SWITCH_REQ_WAITING) {
            switchVideo(CALL_SWITCH_REQ_WAITING, (success, err) => {
               setIsProcessing(false);
               if (err) {
                  logger.log('err', err);
                  showErrorToast(err.message || 'Error occurred');
                  handleCloseAndResetPopup();
               } else {
                  logger.log('success', success);
                  dispatch(updateCallSwitch(success));
               }
            });
         } else if (actionType === CALL_SWITCH_CANCEL) {
            setIsProcessing(false);
            handleCloseAndResetPopup();
         }
      } else if (switchAction === CALL_SWITCH_REQ_WAITING) {
         if (actionType === CALL_SWITCH_CANCEL) {
            switchVideo(CALL_SWITCH_CANCEL, (success, err) => {
               setIsProcessing(false);
               if (err) {
                  logger.log('err', err);
                  handleCloseAndResetPopup();
               } else {
                  logger.log('success', success);
                  handleCloseAndResetPopup();
               }
            });
         }
      } else if (switchAction === CALL_SWITCH_REQUEST) {
         if (actionType === CALL_SWITCH_ACCEPT) {
            switchVideo(CALL_SWITCH_ACCEPT, (success, err) => {
               setIsProcessing(false);
               if (err) {
                  logger.log('err', err);
                  if (err.statusCode === 100607 || err.statusCode === 100606) {
                     showErrorToast(err.message || 'Error occurred');
                     return;
                  } else {
                     handleCloseAndResetPopup();
                  }
               } else {
                  logger.log('success', success);
                  dispatch(setCallType(CALL_TYPE.VIDEO));
                  toggleVideo(true);
                  handleCloseAndResetPopup();
               }
            });
         } else if (actionType === CALL_SWITCH_DECLINE) {
            switchVideo(CALL_SWITCH_DECLINE, (success, err) => {
               setIsProcessing(false);
               if (err) {
                  logger.log(err);
                  showErrorToast(err.message || 'Error occurred');
                  handleCloseAndResetPopup();
               } else {
                  logger.log('success', success);
                  handleCloseAndResetPopup();
               }
            });
         }
      }
   };

   return {
      showPopup,
      callSwitchData,
      switchAction,
      handleSwitchAction,
   };
};
